import { DetailsList, IColumn, Spinner, SpinnerSize } from '@fluentui/react';
import { useEffect, useRef, useState } from "react";
import DocumentTitle from 'react-document-title';
import React from 'react';
import * as Cont from './SampleProfileStyle';
import { theme } from 'highcharts';
import SampleProfileCardsSection from './SampleProfileCardsSection/SampleProfileCardsSection';
import Highstock, { GraphicType } from '../Highstock/Highstock';
import SampleProfileService, { IRequestSampleProfileData, ISampleProfile, Subscription } from '../../services/SampleProfile';
import finametrix from '../../services/FinametrixService';
import { IQuantData, PortfolioCompositionItemType } from '../../services/ManagedPortfolioService';
import scrollToTop from '../../utils/scrollToTop';
import PieHighstock from '../Highstock/PieHighstock';
import Auth from '../../Auth/Auth';
import BackButton from '../buttons/BackButton';
import FirstStepsServices from '../SavingsPlans/FirstStepsServices';
import SavingsPlanService, { SavingsPlanStatuses, SavingsPlanType, SavingsPlanTypes } from '../../services/SavingsPlanService';
import { useHistory } from 'react-router';

interface ISampleProps {
    sampleProfileId: string;
    publicVersion: boolean;
}
const highstockOptions = {
    rangeSelector:
    {
    selected: 6,
    buttons: [
        {
        type: 'year',
        count: 1,
        text: '1y'
        },
        {
        type: 'year',
        count: 2,
        text: '2y'
        },
        {
        type: 'year',
        count: 3,
        text: '3y'
        },
        {
        type: 'year',
        count: 4,
        text: '4y'
        },
        {
        type: 'year',
        count: 5,
        text: '5y'
        },
        {
        type: 'ytd',
        text: 'YTD'
        },
        {
        type: 'all',
        text: 'Todo'
        }
    ]
    },
}

const columns: IColumn[] =
[
    {
        key: 'name',
        name: 'Fondo',
        fieldName: 'name',
        minWidth: 300
    },
    {
        key: 'asset_class',
        name: 'Categoría',
        fieldName: 'category',
        minWidth: 200,
        onRender: (item: PortfolioCompositionItemType) =>
        {
            return <span>{item.category}</span>
        },
    },
    {
        key: 'asset_subclass',
        name: 'Subcategoría',
        fieldName: 'subcategory',
        minWidth: 200,
        onRender: (item: PortfolioCompositionItemType) =>
        {
            return <span>{item.subcategory}</span>
        },
    },
    {
        key: 'weight',
        name: 'Peso',
        fieldName: 'weight',
        minWidth: 100,
        onRender: (item: PortfolioCompositionItemType) =>
        {
            return <span>{item.weight}</span>
        },
    }
];

const SampleProfile = (props: ISampleProps) => {

    const history = useHistory();

    // user
    const user = Auth.getUserProfile();
    // useRef
    const suscribeTargetRef = useRef<HTMLDivElement>(null);

    // Suscripción
    const [subscription, setSubscription] = useState<Subscription>();

    // SampleProfile
    const {sampleProfileId, publicVersion} = props;
    const [sampleProfile, setSampleProfile] = useState<ISampleProfile>();	 
    const [sampleProfileType, setSampleProfileType] = useState<string>("");	

    // SavingsPlan
    const activePlan = Auth.getActivePlan();
    const [isSavingsPlanReady, setIsSavingsPlanReady] = useState<boolean>(false);
    const [savingsPlan, setSavingsPlan] = useState<SavingsPlanType>(
    {
        userId: user.id ? user.id : null,
        name: "",
        periodicContribution: 0,
        periodicContributionPeriod: 1,
        type: SavingsPlanTypes.ExperienceSample,
        status: 0
    });

    // portfolio
    const [porfolioFinametrixId, setPorfolioFinametrixId] = useState<string | null>(null);
    const [serie, setSerie] = useState<any[]>([]);

    // cards
    const [quantData, setQuantData] = useState<IQuantData>();

    // TODO: errores
    const [error, setError] = useState({ message: '', status: false })
    const [errorMessage, setErrorMessage] = useState<string>('');

    // Tabla >> SampleFunds, loaders & sort de columna por peso
    const [sampleFundsData, setSampleFundsData] = useState<PortfolioCompositionItemType[]>([]); 
    const [instrumentIds, setInstrumentIds] = useState<string[]>([]);
    const [isLoadedSampleFundsData, setIsLoadedSampleFundsData] = useState(false);
    const [pieChartSeries, setPieChartSeries] = useState<any[]>([]);

    // Pie Chart
    const [isLoadedPieChart, setIsLoadedPieChart] = useState(false);
    const [isLoadedModelPortfolioSerie, setIsLoadedModelPortfolioSerie] = useState<boolean>(false);


    useEffect(() => {
        scrollToTop();
    }, []);


    //---------------------------------------------------
    // BOTÓN PARA SUSCRIBIRSE: scroll Down
    //---------------------------------------------------
    const goToSuscribe = () => {
        suscribeTargetRef.current?.scrollIntoView({ behavior: 'smooth' });
    };


    //---------------------------------------------------
    // Series
    //---------------------------------------------------
    const transformToDistributionSerie = (data: { category: string; subcategory: string; weight: string }[]) => {
        return data.map(item => ({
            name: item.category,
            value: parseFloat(item.weight.replace("%", "").trim()),
            level2: [
                {
                    name: item.subcategory,
                    value: parseFloat(item.weight.replace("%", "").trim()),
                    level2: null
                }
            ]
        }));
    };

    // *****************************************************************************
    // EFFECTS :: Contratar SampleProfile
    // *****************************************************************************
    const subscribeExperienceSample = async () => {
        setIsSavingsPlanReady(true);
        setErrorMessage('');
    
        try {
            const savingsPlans = await SavingsPlanService.getSavingsPlansAndStatus(user.id);
    
            if (savingsPlans && savingsPlans.length > 0) {
                // En caso de que el usuario ya tenga una suscripción a BlackRock no cancelada
                // se muestra un mensaje de error y se evita la creación de una nueva suscripción
                const hasBlackRockPromotion = savingsPlans.some(plan => 
                    plan.subscriptionId === sampleProfile?.subscription?.id! &&
                    plan.status !== SavingsPlanStatuses.Cancelled
                );
                if (hasBlackRockPromotion) {
                    console.log("El usuario ya tiene una suscripción activa a BlackRock.");
                    setError({ message: 'El usuario ya tiene una suscripción activa a BlackRock', status: false });
                    setErrorMessage('El usuario ya tiene una suscripción activa a BlackRock');
                    setIsSavingsPlanReady(false);
                    return;
                }
            }
    
            const payload: IRequestSampleProfileData = {
                amount: 0,
                reference: `****-SP${sampleProfile?.subscription?.id!}-${new Date().getTime()}`,
                subject: "Carteras Ejemplo",
                customData: {
                    UserId: user.id,
                    ClientData: {
                        email: user.email,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        secondLastName: user.secondLastName!,
                        phonePrefix: user.mainUserIdentity?.signCountryPrefix!,
                        phone: user.mainUserIdentity?.signPhone!,
                    },
                    PaymentData: {
                        amount: sampleProfile?.subscription.price,
                        reference: `****-SP${sampleProfile?.subscription?.id!}-${new Date().getTime()}`,
                        subject: `****-${sampleProfile?.subscription?.id!}`,
                        status: 1,
                        platform: "INESPAY"
                    },
                    SampleProfileData: {
                        Name: sampleProfile?.name!,
                        SubscriptionId: sampleProfile?.subscription?.id!,
                        JsonFundsData: sampleProfile?.jsonFundsData
                    }
                }
            };
    
            const res = await SampleProfileService.postSampleProfileNoPrice(payload);
    
            if (res && res.success && res.savingPlanId) {
                const savingPlanId = res.savingPlanId;
                history.push(`/savingsPlans/${savingPlanId}/selectIdentities`);
            } else {
                setError({ message: 'Error al cargar los datos del portfolio', status: false });
            }
    
        } catch (error) {
            console.error("Error en la suscripción:", error);
            setIsSavingsPlanReady(false);
            setError({ message: 'Error al procesar la solicitud', status: false });
            setErrorMessage('Error al procesar la solicitud');
        }
    };
    


    // *****************************************************************************
    // EFFECTS :: fetchSampleProfileAndDatagrid
    // *****************************************************************************
    const fetchSampleProfileAndDatagrid = async () => {
        let instrumentIds: string[] = [];
    
        try {
          // 1. Obtener SampleProfile
        const sampleProfile = await SampleProfileService.getSampleProfile(sampleProfileId.toString());
    
        if (!sampleProfile) {
            setError({ message: 'Error al cargar los datos del portfolio', status: false });
            return;
        }
    
        setSampleProfile(sampleProfile);
        localStorage.setItem("sampleProfile", JSON.stringify(sampleProfile));
    
          // 2. Procesar los fondos
        if (sampleProfile?.jsonFundsData) {
        let fundsData;
        try {
            fundsData = JSON.parse(sampleProfile.jsonFundsData);
            setSampleProfileType(fundsData?.Riesgo!);
        } catch (err) {
            console.error("Error parseando jsonFundsData:", err);
            setError({ message: 'Error al procesar los fondos', status: false });
            return;
        }
    
            // 3. Obtener AssetClass para cada fondo
            let fundsWithAssetClass = [];
            try {
                fundsWithAssetClass = await Promise.all(
                    fundsData.Funds.map(async (fund: any) => {
                    const instrumentId = fund.Fund.InstrumentId;
                    if (instrumentId) {
                        instrumentIds.push(instrumentId);
                    }
        
                    try {
                        const assetClassData = await finametrix.getInstrumentAssetclass(instrumentId);
                        return {
                            _id: '',
                            instrumentId,
                            weight: fund.Fund.Weight,
                            name: fund.Fund.Name,
                            isin: fund.Fund.ISIN,
                            category: assetClassData?.relationData?.parent?.name || "Sin categoría",
                            subcategory: assetClassData?.relationData?.name || "Sin subcategoría",
                        };
                    } catch (err) {
                        console.error(`Error obteniendo asset class para InstrumentId ${instrumentId}:`, err);
                        return {
                            _id: '',
                            instrumentId,
                            weight: fund.Fund.Weight,
                            name: fund.Fund.Name,
                            isin: fund.Fund.ISIN,
                            category: "Sin categoría",
                            subcategory: "Sin subcategoría"
                        };
                    }
                }))
            } catch (err) {
                console.error("Error procesando fondos:", err);
                setError({ message: 'Error al obtener fondos', status: false });
            }
    
            setInstrumentIds(instrumentIds);
            setSampleFundsData(fundsWithAssetClass);
            setIsLoadedSampleFundsData(true);
    
            // 4. Distribución para el gráfico <PieHighstock />
            try {
                const transformedDistribution = transformToDistributionSerie(fundsWithAssetClass);
                if (transformedDistribution.length > 0) {
                    setPieChartSeries(transformedDistribution);
                    setIsLoadedPieChart(true);
                }
            } catch (err) {
                console.error("Error transformando distribución:", err);
                setError({ message: 'Error al transformar la distribución', status: false });
            }
    
            // 5. Obtener serie para <Highstock />
            if (fundsData?.PorfolioFinametrixId) {
                setPorfolioFinametrixId(fundsData.PorfolioFinametrixId);
    
                try {
                    const sampleProfileForTheCover = await SampleProfileService.getSampleProfileForTheCover(fundsData.PorfolioFinametrixId);
                    if (sampleProfileForTheCover?.data?.length > 0) {
                    setQuantData(sampleProfileForTheCover.quantData);
                    setSerie(sampleProfileForTheCover.data);
                    setIsLoadedModelPortfolioSerie(true);
                    }
                } catch (err) {
                    console.error("Error obteniendo serie para <Highstock />:", err);
                    setError({ message: 'Error al obtener la serie', status: false });
                }
            }
        }
    
          // 6. Suscripción
        if (sampleProfile.subscription) {
            setSubscription(sampleProfile.subscription);
            setIsSavingsPlanReady(true);
            setSavingsPlan(prevState =>
                ( // @ts-ignore
                    { ...prevState, name: sampleProfile.subscription.name }
                ))
        }
    
        } catch (error) {
            console.error('Error general en fetchSampleProfileAndDatagrid:', error);
            setError({ message: 'Error al cargar los datos del portfolio', status: false });
        }
    }
    
    useEffect(() => {
        if (!isNaN(Number(sampleProfileId))) {
            fetchSampleProfileAndDatagrid();
        }
    }, [sampleProfileId]);



    return (
        <DocumentTitle title='IronIA - Carteras Ejemplo'>
            <>
                <React.Fragment>
                    {!publicVersion && <BackButton />}
                    <div>
                        <Cont.RowHeaderMarket className="ms-Grid-row">
                            <Cont.ColHeaderMarket>
                                <h1>Carteras Ejemplo</h1>
                            </Cont.ColHeaderMarket>
                            <Cont.SPButton
                                    text="Contratar"
                                    onClick={() => goToSuscribe()}
                                    
                                />
                        </Cont.RowHeaderMarket>
                        <Cont.MarketLineSeparator className='separator' />
                    </div>



                    <Cont.Separator />



                    <Cont.SPRow className="ms-Grid-row">
                        <Cont.SPCol className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl12 ms-xxl12 ms-xxxl12">
                            <Cont.SPTitle>
                                Cartera de inversiones seleccionada: <span>{sampleProfileType}</span>
                            </Cont.SPTitle>
                        </Cont.SPCol>
                        <Cont.SPCol className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl12 ms-xxl12 ms-xxxl12">
                            <Cont.SPSummary>
                                <div dangerouslySetInnerHTML={{ __html: sampleProfile?.summary! }} />
                            </Cont.SPSummary>
                        </Cont.SPCol>
                    </Cont.SPRow>



                    <Cont.Separator />



                    {/* Cards Section */}
                    <SampleProfileCardsSection 
                        year={(((quantData?.performance1Y ?? 0) * 100).toFixed(2) + " %")} 
                        performance2Year={(((quantData?.performance2Y ?? 0) * 100).toFixed(2) + " %")} 
                        performance3Year={(((quantData?.performance3Y ?? 0) * 100).toFixed(2) + " %")}
                        performance5Year={(((quantData?.performance5Y ?? 0) * 100).toFixed(2) + " %")}
                        ytd={(((quantData?.performanceYTD ?? 0) * 100).toFixed(2) + " %")} 
                        theme={theme} 
                    />



                    <Cont.Separator />
                    


                    {/*Gráfico Highstock */}
                    <Cont.SPRow className="ms-Grid-row">
                        <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl12 ms-xxl12 ms-xxxl12">

                            {!isLoadedModelPortfolioSerie ? (
                                <Spinner size={SpinnerSize.large} /> 
                            ) : isLoadedModelPortfolioSerie ? (
                                <Highstock
                                        series={[
                                            {
                                                "name": sampleProfile?.name,
                                                "value": porfolioFinametrixId,
                                                "data": serie
                                            }
                                        ]}
                                        customOptions={highstockOptions} 
                                        graphicType={GraphicType.moneyGraphic}                        />
                            ) : null}
                        </div>
                    </Cont.SPRow>



                    <Cont.Separator />



                    <Cont.SPRow12 className="ms-Grid-row">
                        <Cont.SPCol60>

                            { sampleFundsData.length === 0 && (
                                <Spinner size={SpinnerSize.large} />
                            )}
                            { sampleFundsData.length !== 0 &&
                            (
                                <DetailsList items={sampleFundsData} columns={columns} selectionMode={0} />
                            )}

                        </Cont.SPCol60>
                        <Cont.SPColSpacer />
                        <Cont.SPCol36>
                            <Cont.SPSubTitle>Tipología de inversiones</Cont.SPSubTitle>
                            {!isLoadedPieChart && (
                                <Spinner size={SpinnerSize.large} />
                            )}
                            {isLoadedPieChart  && (
                                <PieHighstock
                                    chartSerie={pieChartSeries}
                                    showSubcategories={true}
                                />
                            )}
                        </Cont.SPCol36>
                    </Cont.SPRow12>


                    <Cont.Separator />

                    <div ref={suscribeTargetRef}></div>
                    <Cont.SPRow className="ms-Grid-row">
                        <Cont.SPCol className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl12 ms-xxl12 ms-xxxl12">
                            <div className="ms-Grid-row savings-plans options">
                                <FirstStepsServices
                                    savingsPlan={savingsPlan}
                                    savingsPlanIsCompleted={true}
                                    isSavingsPlanReady={isSavingsPlanReady}
                                    user={user}
                                    errorMessage={errorMessage}
                                    subscription={subscription}
                                    publicVersion={publicVersion}
                                    onExperienceSampleSubscribe={() => subscribeExperienceSample()}
                                />
                            </div>
                        </Cont.SPCol>
                    </Cont.SPRow>
                </React.Fragment>
            </>
        </DocumentTitle >
    );
}

export default SampleProfile;